import React, { useState, useEffect, useCallback, useRef } from 'react'
import { useQuery } from '@apollo/client'
import { useParams } from 'react-router-dom'
import gql from 'graphql-tag'
import { BoardQuery } from './Queries'
import { debounce } from 'lodash'
import '../Drivers/DriversNew/DriversListContainer.css'
import './TaskManagementListHeader.css'
import { Row, Col, Input, Button } from "reactstrap"
import NewTaskModal from "./TaskComponents/NewTaskModal"
import NewBoardModal from "./TaskComponents/NewBoardModal"
import AdvancedFilters, { ADVANCED_FILTERS_TRIGGER_ID } from "../Filters/AdvancedFilters"
import AppliedAdvancedFilters from "../Filters/AppliedAdvancedFilters"
import { useAdvancedFilters } from "../Filters/filterUrl"
import withApolloClient from '../withApolloClient'
import { HasPermissionsQuery } from "../Functions/querys"
import TableColumnPreferenceControl from "../Material/TableColumnPreferenceControl"
import ConfirmationPopup from '../Material/ConfirmationPopup'
import { countVisibleFilters } from "../Filters/presetFilters"
import { kanbanGroupedFieldName, getHiddenFilterFields } from "./taskFilterPresets"

const SYNC_INCIDENT_TASKS = gql`
    mutation SyncIncidentTasks($input: SyncIncidentTasksMutationInput!) {
        syncIncidentTasks(input: $input) {
            ok
            errors {
                field
                messages
            }
        }
    }
`

function KanbanSettingsButton({ boardId, groupBy, onGroupByChange }) {
    const [showSettings, setShowSettings] = useState(false)
    const settingsRef = useRef(null)
    const { data } = useQuery(BoardQuery, { variables: { id: boardId }, skip: !boardId, fetchPolicy: 'cache-first' })
    const enumColumns = data?.board?.columnSet?.edges
        ?.filter(({ node }) => node.columnType === 'ENUM' && !node.isArchived)
        .map(({ node }) => node) || []

    useEffect(() => {
        if (!showSettings) return
        const handler = (e) => { if (settingsRef.current && !settingsRef.current.contains(e.target)) setShowSettings(false) }
        document.addEventListener('mousedown', handler)
        return () => document.removeEventListener('mousedown', handler)
    }, [showSettings])

    return (
        <span ref={settingsRef} style={{ position: 'relative', display: 'inline-block', marginLeft: '4px', verticalAlign: 'middle' }}>
            <Button size="sm" color="secondary" className="mr-2" outline={!showSettings} onClick={() => setShowSettings(v => !v)} title="Configure kanban columns">
                <i className="fa fa-cog" />
            </Button>
            {showSettings && (
                <div className="kanban-settings-dropdown" style={{ right: 0, left: 'auto', top: 'calc(100% + 4px)', zIndex: 1050 }}>
                    <div className={`kanban-settings-option${groupBy === 'status' ? ' active' : ''}`} onClick={() => { onGroupByChange('status'); setShowSettings(false) }}>Status</div>
                    {enumColumns.map(col => (
                        <div key={col.pk} className={`kanban-settings-option${String(groupBy) === String(col.pk) ? ' active' : ''}`} onClick={() => { onGroupByChange(String(col.pk)); setShowSettings(false) }}>
                            {col.title}
                        </div>
                    ))}
                </div>
            )}
        </span>
    )
}

// onApplyFilters lets a page own the write: the task container folds the popup's
// preset and user-filter halves into a single ?filter=. Without it, Apply writes
// the user filters straight to the URL, which is what every other page wants.
const TaskManagementListHeader = ({ filterValues, setFilterValues, presets = [], onPresetsChange = () => {}, onApplyFilters, showNewBoardButton, parentBoardId, viewType, onViewTypeChange, groupBy, onGroupByChange, defaultColumnConfig, columnConfig, setColumnConfig, tableId, ...props }) => {
    const [openModal, setOpenModal] = useState("")
    const [syncTasksLoading, setSyncTasksLoading] = useState(false)
    const [syncTasksError, setSyncTasksError] = useState("")
    const safeFilterValues = filterValues || {}
    const [advancedFilters, setAdvancedFilters] = useAdvancedFilters()

    let { boardId } = useParams()
    if (parentBoardId) {
        boardId = parentBoardId
    }
    const advancedFiltersContext = boardId ? { boardId } : null

    const updateSearchTerm = useCallback(debounce((searchTerm) => { setFilterValues({ ...safeFilterValues, searchTerm }) }, 500), [safeFilterValues])

    const { data: hasPermissions, loading: hasPermissionsLoading } = useQuery(HasPermissionsQuery, {
        variables: {
            userPermissions: ["add_board", "add_taskrabbit", "custom_can_sync_incident_tasks"]
        }
    })

    // The kanban columns ARE the values of whatever it groups by -- status, or a
    // custom ENUM column -- so a filter on that field cannot mean anything here.
    // Hidden from the popup (unpickable), from the chips, and from the count, since
    // the query strips it too: a filter you cannot see, cannot remove, and that
    // changes nothing is worse than no filter at all. It stays in the URL and
    // applies again in list view, or when grouping by something else.
    const groupedFilterField = kanbanGroupedFieldName(viewType, groupBy)
    const hiddenFilterFields = getHiddenFilterFields(boardId, viewType, groupBy)
    const visibleFilterCount = countVisibleFilters(
        presets.filter((preset) => preset.fieldName !== groupedFilterField),
        (advancedFilters || []).filter((filter) => filter?.fieldName !== groupedFilterField),
    )

    if (!props.apolloClient) {
        return null
    }


    const toggleModal = (modalName) => {
        setOpenModal(openModal === modalName ? "" : modalName)
    }

    const syncIncidentTasks = async () => {
        setSyncTasksLoading(true)
        setSyncTasksError("")
        try {
            const result = await props.apolloClient.mutate({
                mutation: SYNC_INCIDENT_TASKS,
                variables: { input: { incidentId: props.incidentId } },
                refetchQueries: ["AllTasks"],
                awaitRefetchQueries: true,
            })
            const response = result?.data?.syncIncidentTasks
            if (response?.ok) {
                toggleModal("")
            } else {
                setSyncTasksError(response?.errors?.[0]?.messages?.[0] || "Unable to sync incident tasks.")
            }
        } catch (error) {
            setSyncTasksError(error.message)
        } finally {
            setSyncTasksLoading(false)
        }
    }

    return (
        <Row>
            {openModal === "NewTaskModal" && (
                <NewTaskModal
                    isOpen={openModal === "NewTaskModal"}
                    closeModal={() => toggleModal("")}
                    boardId={boardId}
                    filterValues={safeFilterValues}
                />
            )}
            {openModal === "NewBoardModal" && (
                <NewBoardModal
                    isOpen={openModal === "NewBoardModal"}
                    closeModal={() => toggleModal("")}
                />
            )}
            {openModal === "SyncTasksModal" && (
                <ConfirmationPopup
                    open={openModal === "SyncTasksModal"}
                    message="Are you sure you want to sync tasks for this incident?"
                    confirmAction={syncIncidentTasks}
                    action="Yes, Sync Tasks"
                    color="success"
                    loading={syncTasksLoading}
                    error={syncTasksError}
                    handleClose={() => toggleModal("")}
                />
            )
            }
            {openModal === "AdvancedFilters" && (
                <AdvancedFilters
                    isOpen={openModal === "AdvancedFilters"}
                    onClose={() => toggleModal("")}
                    entity="task"
                    context={advancedFiltersContext}
                    activeFilters={advancedFilters || []}
                    onApply={onApplyFilters || ((newFilters) => setAdvancedFilters(newFilters))}
                    presets={presets}
                    onPresetsChange={onPresetsChange}
                    hiddenFields={hiddenFilterFields}
                />
            )}

            <Col xs={6} className="px-0">
                <span className="driver-search-filter">
                    <Input type="text" name="searchTerm" placeholder="Search" onChange={(e) => updateSearchTerm(e.target.value)} />
                    <i className="fa fa-lg fa-search search-icon" aria-hidden="true"></i>
                </span>
            </Col>
            <Col xs={6} className="text-right mt-2 px-0">
                <div className="task-management-header-actions">
                    {props.incidentId && hasPermissions?.hasPermissions?.includes("custom_can_sync_incident_tasks") && <>
                        <a className="driver-list-button task-management-action-link" onClick={() => toggleModal("SyncTasksModal")}>
                            Sync Tasks | <i className="fa fa-refresh" aria-hidden="true"></i>
                        </a>
                    </>}
                    {hasPermissions?.hasPermissions?.includes("add_taskrabbit") && <>
                        <a className="driver-list-button task-management-action-link" onClick={() => toggleModal("NewTaskModal")}>
                            New Task | <i className="fa fa-plus-square" aria-hidden="true"></i>
                        </a>
                    </>}
                    {showNewBoardButton && hasPermissions?.hasPermissions?.includes("add_board") && <>
                        <a className="driver-list-button task-management-action-link" onClick={() => toggleModal("NewBoardModal")}>
                            New Board | <i className="fa fa-plus-square" aria-hidden="true"></i>
                        </a>
                    </>}

                    {viewType !== "kanban" && columnConfig && columnConfig.length > 0 && <>
                        <TableColumnPreferenceControl
                            tableId={tableId}
                            defaultColumnConfig={defaultColumnConfig}
                            columnConfig={columnConfig}
                            setColumnConfig={setColumnConfig}
                            buttonLabel="Preference"
                        />
                    </>
                    }
                    <a id={ADVANCED_FILTERS_TRIGGER_ID} className="driver-list-button task-management-action-link" onClick={() => toggleModal("AdvancedFilters")}>
                        Advanced Filters | <i className="fa fa-sliders" aria-hidden="true"></i> {visibleFilterCount}
                    </a>
                </div>
            </Col>
            <Col xs={12} className="mt-1 mb-4 px-0">
                <div className="task-management-bottom-row">
                    <div className="task-management-applied-filters">
                        <AppliedAdvancedFilters entity="task" context={advancedFiltersContext} presets={presets} onPresetsChange={onPresetsChange} hiddenFields={hiddenFilterFields} />
                    </div>
                    {onViewTypeChange && (
                        <div className="task-management-view-toggle-row">
                            <div className="task-management-view-toggle" role="group" aria-label="Task View Type">
                                <button
                                    type="button"
                                    className={`task-management-view-toggle-btn ${viewType === "list" ? "active" : ""}`}
                                    onClick={() => onViewTypeChange("list")}
                                    title="List View"
                                >
                                    <i className="fa fa-list" aria-hidden="true" />
                                </button>
                                <button
                                    type="button"
                                    className={`task-management-view-toggle-btn ${viewType === "kanban" ? "active" : ""}`}
                                    onClick={() => onViewTypeChange("kanban")}
                                    title="Kanban View"
                                >
                                    <i className="fa fa-columns" aria-hidden="true" />
                                </button>
                            </div>
                            {viewType === "kanban" && boardId && onGroupByChange && (
                                <KanbanSettingsButton boardId={boardId} groupBy={groupBy} onGroupByChange={onGroupByChange} />
                            )}
                        </div>
                    )}
                </div>
            </Col>
        </Row>
    )
}

export default (withApolloClient(TaskManagementListHeader))
